export type Band = "A" | "B" | "C" | "D" | "E";
export type UnitType = "reading" | "lessons" | "xp";

export interface BandDefaults {
  band: Band;
  label: string;
  grades: string;
  unitType: UnitType;
  dailyTarget: number;
  lessonRate: number;
  currencyName: string;
  masteryBonus: number;
  tierCosts: [number, number, number, number];
}

// Tier prices are expressed as DAYS OF EFFORT, then multiplied by DAY_VALUE.
// The cadence is the thing worth designing; the price tag is arithmetic.
//
//   Tier 1 =  5 days · about a week and a half of real life. The rung a kid
//             must actually reach, because the first prize they get is what
//             teaches them the loop is real. Everything else is decoration
//             until that happens.
//   Tier 2 = 10 days · a few weeks.
//   Tier 3 = 20 days · about six weeks.
//   Tier 4 = 45 days · about a term.
//
// Each rung is roughly double the last, which is a ladder a seven-year-old can
// hold in their head. The previous shape was 5/20/60/180: four times, then
// three, then three, and a tier 4 that took a whole school year of near-perfect
// attendance. A prize almost nobody reaches is not the top of a ladder, it is
// scenery. This version puts the big one within about a term, so it gets won.
//
// Note this measures effort, not calendar time. A kid whose daily target is
// two lessons instead of one earns twice as fast and reaches tier 1 in half
// the days, having done exactly the same amount of work. That is correct.
export const TIER_DAYS: [number, number, number, number] = [5, 10, 20, 45];

export const BAND_DEFAULTS: Record<Band, BandDefaults> = {
  A: { band: "A", label: "K4 to K5", grades: "K4–K5", unitType: "xp", dailyTarget: 25, lessonRate: 0, currencyName: "Study Bucks", masteryBonus: 0, tierCosts: [250, 500, 1000, 2250] },
  B: { band: "B", label: "1st to 2nd grade", grades: "1st–2nd", unitType: "xp", dailyTarget: 25, lessonRate: 0, currencyName: "Study Bucks", masteryBonus: 250, tierCosts: [250, 500, 1000, 2250] },
  C: { band: "C", label: "3rd to 5th grade", grades: "3rd–5th", unitType: "xp", dailyTarget: 35, lessonRate: 0, currencyName: "Study Bucks", masteryBonus: 250, tierCosts: [250, 500, 1000, 2250] },
  D: { band: "D", label: "6th to 8th grade", grades: "6th–8th", unitType: "xp", dailyTarget: 50, lessonRate: 0, currencyName: "Study Bucks", masteryBonus: 375, tierCosts: [250, 500, 1000, 2250] },
  E: { band: "E", label: "9th to 12th grade", grades: "9th–12th", unitType: "xp", dailyTarget: 75, lessonRate: 0, currencyName: "Study Bucks", masteryBonus: 375, tierCosts: [250, 500, 1000, 2250] },
};

// What a student banks on a day they hit their target exactly. Exported so the
// tier arithmetic above can be asserted rather than trusted.
export function dailyEarnAtTarget(band: Band): number {
  const defaults = BAND_DEFAULTS[band];
  return calculateEarnings(defaults.dailyTarget, defaults.unitType, defaults.dailyTarget, defaults.lessonRate).total;
}

export type PrizeCategory = "autonomy" | "food" | "stuff" | "experience" | "one_on_one" | "adventure" | "grand";

export const CATEGORY_LABELS: Record<PrizeCategory, string> = {
  autonomy: "Autonomy",
  food: "Food & treats",
  stuff: "Stuff",
  experience: "Experiences",
  one_on_one: "One-on-one time",
  adventure: "Adventures",
  grand: "Grand prizes",
};

// Cost derives from the item's tier. Since a day of effort is worth the same
// to every child, a tier costs the same in every band; what differs is what a
// child has to do to earn a day. Edit BAND_DEFAULTS.tierCosts to change it.
export function derivedCost(band: Band, tier: number): number {
  const clamped = Math.min(4, Math.max(1, Math.floor(tier)));
  return BAND_DEFAULTS[band].tierCosts[(clamped - 1) as 0 | 1 | 2 | 3];
}

// Legacy custom prizes keep their stored cost; everything else derives.
export function effectiveCost(prize: { tier: number; cost: number; source?: string | null; reward_price?:number|null }, band: Band): number {
  if(prize.reward_price&&prize.reward_price>0)return prize.reward_price;
  if (prize.source === "custom" && prize.cost > 0) return prize.cost;
  return derivedCost(band, prize.tier);
}

// Repinning the expedition goal is limited to once per 7 days.
export const REPIN_COOLDOWN_DAYS = 7;

export interface Earnings {
  earn: number;
  surplus: number;
  total: number;
}

// Historical conversion only, before engagement economy adoption. New currency uses engagement milestones.
// Prize prices remain in Stamps; changing an XP goal never changes its rate.
export const DAY_VALUE = 50;

// Legacy reading/lesson extra work retains its established multiplier.
export const SURPLUS_MULTIPLIER = 1.5;

// Legacy reading/lesson rewards are capped at one extra day. Server-awarded
// XP has no currency multiplier or surplus cap: each XP is exactly one Stamp.
export const MAX_SURPLUS_DAYS = 1;

export function calculateEarnings(
  amount: number,
  unitType: UnitType,
  target: number,
  _lessonRate = 0,
): Earnings {
  const safeAmount = Math.max(0, Math.floor(amount));
  // XP is currency at face value. The daily goal unlocks ski runs; it does
  // not change the exchange rate, multiply extra effort or award a cash bonus.
  if (unitType === 'xp') return {earn:safeAmount,surplus:0,total:safeAmount};
  const safeTarget = Math.max(1, Math.floor(target));

  const atTarget = Math.min(safeAmount, safeTarget);
  const beyond = Math.min(Math.max(0, safeAmount - safeTarget), safeTarget * MAX_SURPLUS_DAYS);
  // Floors throughout, so effort can never round up into currency nobody
  // actually earned.
  const earn = Math.floor((atTarget / safeTarget) * DAY_VALUE);
  const surplus = Math.floor((beyond / safeTarget) * DAY_VALUE * SURPLUS_MULTIPLIER);
  return { earn, surplus, total: earn + surplus };
}

export function unitLabel(unitType: UnitType, amount = 2): string {
  if (unitType === "xp") return "XP";
  if (unitType === "lessons") return amount === 1 ? "lesson" : "lessons";
  return amount === 1 ? "reading night" : "reading nights";
}

export function clampPercent(amount: number, target: number): number {
  return Math.max(0, Math.min(100, Math.round((amount / Math.max(1, target)) * 100)));
}

// ---- The season climb ----
//
// A third counter, deliberately distinct from the two that already exist.
//   streak   resets the moment a day is missed
//   balance  goes down every time a prize is bought
//   season   only ever goes up, all school year
//
// Kids need one number that nothing can take away, or every mechanic in the
// app is something they can lose. This is that number, and spending never
// touches it: buying a prize costs currency, not altitude.
// 225 feet a climbing day puts Everest at 129 days, which is 3.6 days a week
// across a 36-week year. That slack is the whole point. At 200 it needed
// exactly 4.0 days every single week with none to spare, so a child who
// climbed four days a week and caught flu twice missed the summit of their
// year by a day or two. Cutting it that fine is not motivating, it is cruel.
export const FEET_PER_CLIMBING_DAY = 225;

export interface SeasonWaypoint {
  feet: number;
  label: string;
  where: string;
  badge: string;
  blurb: string;
}

// The ladder walks outward from their own backyard to the roof of the world,
// which gives the year a story rather than a number line. Table Rock is a
// forty minute drive and is also a tier 3 prize in the catalog, so the first
// summit is somewhere they may actually stand.
export const SEASON_WAYPOINTS: SeasonWaypoint[] = [
  { feet: 3_124, label: "Table Rock", where: "South Carolina", badge: "🥾", blurb: "The one you can see from the car." },
  { feet: 6_684, label: "Mt. Mitchell", where: "North Carolina", badge: "🌲", blurb: "Highest point east of the Mississippi." },
  { feet: 14_115, label: "Pikes Peak", where: "Colorado", badge: "🏔️", blurb: "Where they wrote America the Beautiful." },
  { feet: 20_310, label: "Denali", where: "Alaska", badge: "❄️", blurb: "The highest in all of North America." },
  { feet: 29_032, label: "Everest", where: "Nepal · Tibet", badge: "👑", blurb: "The top of the whole world." },
];

// Past Everest there is nothing higher to climb, so the scale changes rather
// than the ladder ending. These sit inside what a genuinely relentless year
// reaches (180 days is 40,500 ft), so they are a reward for the kid who ran
// out of mountain, not a taunt.
export const BONUS_WAYPOINTS: SeasonWaypoint[] = [
  { feet: 33_000, label: "Cruising altitude", where: "Where the jets are", badge: "✈️", blurb: "You are level with the planes now." },
  { feet: 35_876, label: "Challenger Deep", where: "Upside down", badge: "🌊", blurb: "You climbed the depth of the deepest place in the ocean." },
  { feet: 40_000, label: "The stratosphere", where: "Above the weather", badge: "🚀", blurb: "Above the clouds, above the storms, off the map." },
];

// Everything a kid could reach, in order. The bonus tier only appears once
// Everest is behind them, so the year does not look infinite in September.
export function waypointsFor(feet: number): SeasonWaypoint[] {
  const everest = SEASON_WAYPOINTS[SEASON_WAYPOINTS.length - 1]?.feet ?? Infinity;
  return feet >= everest ? [...SEASON_WAYPOINTS, ...BONUS_WAYPOINTS] : SEASON_WAYPOINTS;
}

export function seasonProgress(climbingDays: number): {
  feet: number; next: SeasonWaypoint | null; passed: SeasonWaypoint[]; percentToNext: number;
} {
  const feet = Math.max(0, Math.floor(climbingDays)) * FEET_PER_CLIMBING_DAY;
  const ladder = waypointsFor(feet);
  const passed = ladder.filter((point) => feet >= point.feet);
  const next = ladder.find((point) => feet < point.feet) ?? null;
  const floor = passed.length ? (passed[passed.length - 1]?.feet ?? 0) : 0;
  const percentToNext = next ? clampPercent(feet - floor, next.feet - floor) : 100;
  return { feet, next, passed, percentToNext };
}

// The peak a season actually reached. A year that ends at Denali ended at
// Denali; it did not fail to reach Everest. Nothing in this app frames effort
// as a shortfall, and the last day of the year is not the place to start.
export function seasonSummit(climbingDays: number): SeasonWaypoint | null {
  const { passed } = seasonProgress(climbingDays);
  return passed[passed.length - 1] ?? null;
}

// A school year, not a calendar one. Rolls over on 1 August.
export function seasonStart(today: string): string {
  const [year, month] = today.split("-").map(Number);
  const startYear = (month ?? 1) >= 8 ? (year ?? 0) : (year ?? 0) - 1;
  return `${startYear}-08-01`;
}

// ---- Savings vault ----
//
// Patience is rewarded; impatience is never punished. Withdrawing early
// returns exactly the deposit, so the worst case is a bonus a kid never had
// rather than currency taken from one they did.
export const VAULT_MATURITY_DAYS = 30;

// Ten percent, chosen for teachability over generosity.
//
// It is the one percentage a child can compute in their head forever: move the
// decimal one place. Every other rate they will ever meet is built from it
// (5% is half, 20% is double, 15% is one and a half), so the kid who learns
// this one has the tool for all of them.
//
// The floor matters at small balances, and it is a real constraint rather than
// a rounding detail. At 5% a kid must save 20 before the bonus is even 1; at
// 10% it pays from 10 saved, which is a fraction of a single day.
export const VAULT_BONUS_RATE = 0.1;

// Round amounts a kid can see the arithmetic of at a glance, used to show the
// worked example rather than just asserting that saving is good.
export const VAULT_EXAMPLES = [50, 125, 250, 500];

// Floor, so the bonus can never round in a way that invents currency.
export function vaultBonus(amount: number): number {
  return Math.floor(Math.max(0, Math.floor(amount)) * VAULT_BONUS_RATE);
}

export function vaultMaturesAt(depositedAtIso: string): string {
  return new Date(new Date(depositedAtIso).getTime() + VAULT_MATURITY_DAYS * 86_400_000).toISOString();
}

export function vaultDaysLeft(maturesAtIso: string, nowMs: number): number {
  return Math.max(0, Math.ceil((new Date(maturesAtIso).getTime() - nowMs) / 86_400_000));
}

// What a deposit is worth if it comes out right now.
export function vaultWithdrawalValue(
  deposit: { amount: number; matures_at: string },
  nowMs: number,
): { amount: number; bonus: number; total: number; matured: boolean } {
  const matured = new Date(deposit.matures_at).getTime() <= nowMs;
  const bonus = matured ? vaultBonus(deposit.amount) : 0;
  return { amount: deposit.amount, bonus, total: deposit.amount + bonus, matured };
}
